import { GoTrueClient } from '@supabase/auth-js';

/**
 * Browser sign-in.
 *
 * One service serves the app and the API from the same origin, so there is no
 * gateway in front to establish an identity. The browser signs in against
 * Supabase itself and sends the access token it is issued as
 * `Authorization: Bearer <jwt>`; server/src/services/authSupabase.js verifies
 * that signature against the project's published JWKS.
 *
 * The configuration arrives from GET /api/health at boot rather than being
 * baked into the bundle at build time, so one build runs against any
 * deployment and local development -- where auth is off entirely -- needs no
 * second set of variables. That endpoint sits outside the auth hook on
 * purpose, which is what lets a signed-out page read it. Only the publishable
 * key travels: identifying the project is the whole job it has.
 *
 * This is `@supabase/auth-js`, not `@supabase/supabase-js`. Measured on this
 * repo the full SDK costs 57.8 kB gzip against auth-js's 24.7 kB, and the
 * browser never reaches the database or the bucket directly -- uploads go to
 * signed URLs the API mints -- so the difference buys nothing.
 */

/** Labels for the providers a deployment may offer. */
const PROVIDER_LABELS = {
  google: 'Continue with Google',
  apple: 'Continue with Apple',
};

/**
 * @typedef {object} AuthState
 * @property {'loading'|'off'|'signed-out'|'signed-in'|'unconfigured'|'unreachable'} status
 * @property {string[]} providers  Sign-in providers this deployment offers.
 * @property {object|null} user    The Supabase user, when signed in.
 * @property {string|null} message Why sign-in cannot proceed, for the two bad states.
 */

let state = { status: 'loading', providers: [], user: null, message: null };
let client = null;
let bootPromise = null;

const listeners = new Set();

function setState(next) {
  state = { ...state, ...next };
  for (const listener of listeners) listener(state);
}

export function authState() {
  return state;
}

/** Subscribe to state changes. Returns the unsubscribe function. */
export function subscribeToAuth(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * A project ref scopes the stored session, so two deployments open in one
 * browser cannot overwrite each other's sign-in — which is exactly what happens
 * when a laptop has both the deployed app and a cloud-backed local run open.
 */
function storageKeyFor(supabaseUrl) {
  const ref = new URL(supabaseUrl).hostname.split('.')[0];
  return `pantry.auth.${ref}`;
}

/**
 * Read the deployment's auth configuration.
 *
 * Deliberately a bare `fetch` rather than the api client: this runs before any
 * token exists, and api.js calls back into this module on a 401.
 */
async function fetchAuthConfig() {
  const response = await fetch('/api/health');
  if (!response.ok) throw new Error(`Health check failed (${response.status}).`);
  const health = await response.json();
  return health?.auth ?? { required: false };
}

/**
 * Work out whether sign-in is needed, and if so restore or resume the session.
 * Idempotent: every caller shares the first boot.
 */
export function initAuth() {
  if (!bootPromise) bootPromise = boot();
  return bootPromise;
}

async function boot() {
  let auth;
  try {
    auth = await fetchAuthConfig();
  } catch {
    // The app shell loaded, so the server answered something. Treat this as a
    // transient failure the user can retry rather than as "signed out", which
    // would silently discard a perfectly good session.
    setState({
      status: 'unreachable',
      message: 'Could not reach the server to check whether sign-in is needed.',
    });
    return state;
  }

  if (!auth.required) {
    setState({ status: 'off', providers: [], user: null, message: null });
    return state;
  }

  if (!auth.supabaseUrl || !auth.publishableKey) {
    // Auth is switched on but the project is half-configured. Saying so beats
    // rendering a sign-in button that cannot work.
    setState({
      status: 'unconfigured',
      providers: [],
      message: auth.supabaseUrl
        ? 'This deployment requires sign-in but is missing PANTRY_SUPABASE_PUBLISHABLE_KEY.'
        : 'This deployment requires sign-in but no Supabase project is configured.',
    });
    return state;
  }

  // Anything that throws in here — a project URL that will not parse, storage
  // the browser refuses — has to end in a state that says so. An escaped
  // rejection would leave `bootPromise` rejected and the app on its spinner
  // for as long as the tab is open.
  try {
    client = new GoTrueClient({
      url: `${auth.supabaseUrl}/auth/v1`,
      headers: { apikey: auth.publishableKey },
      storageKey: storageKeyFor(auth.supabaseUrl),
      // PKCE keeps the token out of the URL fragment: the redirect carries a
      // single-use code that only this browser holds the verifier for. auth-js
      // strips the code from the address bar once it has exchanged it, so a
      // deep link survives a sign-in round trip intact.
      flowType: 'pkce',
      detectSessionInUrl: true,
      autoRefreshToken: true,
      persistSession: true,
    });

    // Registered before the first read so the post-redirect exchange, which
    // finishes asynchronously, cannot land between the read and this
    // subscription.
    client.onAuthStateChange((event, session) => {
      setState({
        status: session ? 'signed-in' : 'signed-out',
        user: session?.user ?? null,
        // A fresh sign-in clears whatever refusal was on screen. A sign-out
        // does not: the reason someone was signed out is worth reading.
        message: event === 'SIGNED_IN' ? null : state.message,
      });
    });

    const { data } = await client.getSession();
    setState({
      status: data?.session ? 'signed-in' : 'signed-out',
      providers: auth.providers ?? [],
      user: data?.session?.user ?? null,
    });
  } catch (error) {
    client = null;
    setState({
      status: 'unconfigured',
      providers: [],
      message: `Sign-in could not be set up: ${error.message}`,
    });
  }
  return state;
}

/**
 * The token for the current request, or null when there is no session.
 *
 * `getSession()` refreshes a token that is at or near expiry, so this is also
 * what keeps a long-lived tab from drifting into 401s.
 */
export async function accessToken() {
  if (!client) return null;
  const { data } = await client.getSession();
  return data?.session?.access_token ?? null;
}

/**
 * Called by api.js when the server rejects the token. The session is over, so
 * clear it locally and let the app render its sign-in screen.
 *
 * Local scope only: a global sign-out revokes the refresh token for every
 * device, and one expired tab is no reason to sign a phone out of the kitchen.
 */
export async function sessionRejected(message) {
  setState({ message: message || null });
  if (client) {
    await client.signOut({ scope: 'local' });
    return;
  }
  // No client means this page booted believing sign-in was not required, and
  // the server has since disagreed — it was restarted with auth switched on.
  // Re-read the configuration rather than showing a sign-in screen with no
  // providers on it. `reboot` keeps the message just set; only a user-initiated
  // retry clears it.
  await reboot();
}

/** Send the browser to a provider, returning to the page it started on. */
export async function signInWith(provider) {
  if (!client) throw new Error('Sign-in is not configured for this deployment.');
  const { data, error } = await client.signInWithOAuth({
    provider,
    options: {
      // Back to the current screen, not the home page. The Supabase project's
      // redirect allowlist has to cover it — `<origin>/**` is what that takes.
      redirectTo: window.location.origin + window.location.pathname + window.location.search,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw new Error(error.message || 'Could not start sign-in.');
  if (!data?.url) throw new Error('The sign-in provider returned no address to visit.');
  window.location.assign(data.url);
}

/**
 * Sign in with an email address.
 *
 * Mail rather than a password: nothing to store, nothing to reset, and no
 * password to be reused from somewhere that has been breached. One call sends
 * one message carrying both ways in -- a link that comes back here with a
 * session on it, and the numeric code `verifyEmailCode` below takes. Which of
 * the two the message actually shows is the project's email template, not
 * anything decided here: the code appears when the template includes
 * `{{ .Token }}`.
 *
 * `shouldCreateUser` is left at its default. Whether a stranger can make an
 * account is the access rules' decision, made server-side after the token is
 * verified -- an allowlist that only worked when the person had never signed in
 * before would be no allowlist at all.
 */
export async function signInWithEmail(email) {
  if (!client) throw new Error('Sign-in is not configured for this deployment.');
  const address = String(email || '').trim();
  if (!address) throw new Error('Enter an email address.');

  const { error } = await client.signInWithOtp({
    email: address,
    options: {
      // Back to the page this started on, same as the OAuth path. The Supabase
      // project's redirect allowlist has to cover it -- `<origin>/**`.
      emailRedirectTo: window.location.origin + window.location.pathname + window.location.search,
    },
  });
  if (error) throw new Error(error.message || 'Could not send the sign-in email.');
  return address;
}

/**
 * Finish an email sign-in with the code from the message, in this page, with no
 * redirect.
 *
 * The link in the same message still works, and on a laptop it is the easier of
 * the two. What it cannot do is finish inside an installed app: Android hands a
 * `<project>.supabase.co` link to Chrome rather than to the app packaged for
 * Play, and an iOS home-screen install bounces out to Safari the same way, so
 * the person ends up signed in inside a browser standing next to the app they
 * were using. A code is typed where it was asked for, so nothing moves.
 *
 * Because nothing navigated there is also nothing to restore: the
 * `onAuthStateChange` subscription registered in `boot()` flips the state to
 * `signed-in` and the gate renders the app around whatever route was already
 * open. Preserving that route is what the `emailRedirectTo` dance above exists
 * for, and here it costs nothing.
 *
 * PKCE is not involved. `flowType` governs the single-use code a *redirect*
 * carries, which only this browser holds the verifier for; a one-time password
 * is verified by its value, so this path needs no verifier and no entry in the
 * project's redirect allowlist.
 *
 * Resolves with nothing. The state change is the result.
 */
export async function verifyEmailCode(email, code) {
  if (!client) throw new Error('Sign-in is not configured for this deployment.');
  const address = String(email || '').trim();
  if (!address) throw new Error('Enter an email address.');

  // Whitespace is the only thing removed. A code copied out of a mail app
  // arrives often enough with a space in the middle or a newline on the end,
  // and neither was ever part of it. Everything else is left as typed:
  // somebody who pasted the wrong thing entirely is told so here, rather than
  // having it quietly reshaped into digits nobody sent them.
  //
  // Six to ten digits, not six. The code's length is a setting on the Supabase
  // project (Authentication -> Email -> OTP length), not a property of the
  // protocol, and this shipped once insisting on six against a project that
  // sends eight -- so the first person to try it was refused a correct code.
  // Anything Supabase can be configured to send is accepted here; the server
  // is the one that knows the length, and it says no to a wrong one.
  const token = String(code || '').replace(/\s+/g, '');
  if (!/^\d{6,10}$/.test(token)) throw new Error('Enter the code from the email: six to ten digits, nothing else.');

  const { error } = await client.verifyOtp({ email: address, token, type: 'email' });
  if (error) throw new Error(codeErrorMessage(error));
}

/**
 * One Supabase failure covers three situations -- the code was mistyped, it was
 * already used, or it has expired -- and its wording, "Token has expired or is
 * invalid", tells a person holding a phone none of what to do about any of
 * them. That one is rewritten. Every other failure is passed through in the
 * service's own words: an odd but accurate sentence beats a guess dressed up as
 * a helpful one.
 */
function codeErrorMessage(error) {
  const message = error?.message || '';
  if (error?.code === 'otp_expired' || /expired or is invalid/i.test(message)) {
    return 'That code did not work. Each code works once and expires'
      + ' — check for a newer email, or request another.';
  }
  return message || 'Could not check that code.';
}

export async function signOut() {
  if (!client) return;
  await client.signOut();
  setState({ status: 'signed-out', user: null, message: null });
}

function reboot() {
  bootPromise = null;
  setState({ status: 'loading' });
  return initAuth();
}

/** Retry the boot sequence after it failed to reach the server. */
export function retryAuthBoot() {
  setState({ message: null });
  return reboot();
}

export function providerLabel(provider) {
  return PROVIDER_LABELS[provider]
    || `Continue with ${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
}
